/**
 * Represents a group of faces sharing a material with triplanar projection
 */
export default class MaterialGroup {
  static nextId = 1;

  /**
   * Create a new material group
   * @param {Object} options - Group options
   * @param {string} options.id - Unique group ID
   * @param {string} options.name - Group name
   * @param {Material} options.material - Base material for the group
   */
  constructor(options = {}) {
    this.id = options.id || `group_${MaterialGroup.nextId++}`;
    this.name = options.name || `Group ${this.id}`;
    this.material = options.material || null;
    this.faces = new Set(); // Set of "cuboidKey|face" strings

    // === Texture settings ===
    this.textureScale = options.textureScale || { x: 1.0, y: 1.0, z: 1.0 };
    // Convert legacy single number to object
    if (typeof this.textureScale === 'number') {
      this.textureScale = { x: this.textureScale, y: this.textureScale, z: this.textureScale };
    }
    this.textureScaleLinked = options.textureScaleLinked !== false; // Default linked
    this.textureOffset = options.textureOffset || { x: 0, y: 0 };
    this.textureWidth = options.textureWidth || 256;
    this.textureHeight = options.textureHeight || 256;

    // === Video settings ===
    this.videoScale = options.videoScale || { x: 1.0, y: 1.0, z: 1.0 };
    if (typeof this.videoScale === 'number') {
      this.videoScale = { x: this.videoScale, y: this.videoScale, z: this.videoScale };
    }
    this.videoScaleLinked = options.videoScaleLinked !== false;
    this.videoOffset = options.videoOffset || { x: 0, y: 0 };

    // === Gradient settings ===
    this.gradientScale = options.gradientScale || { x: 1.0, y: 1.0, z: 1.0 };
    if (typeof this.gradientScale === 'number') {
      this.gradientScale = { x: this.gradientScale, y: this.gradientScale, z: this.gradientScale };
    }
    this.gradientScaleLinked = options.gradientScaleLinked !== false;
    this.gradientAngle = options.gradientAngle || 0; // Base rotation angle in degrees
    this.gradientAxis = options.gradientAxis || 'y'; // Primary axis for gradient: 'x', 'y', or 'z'
    this.gradientOffset = options.gradientOffset || { x: 0, y: 0, z: 0 };

    // === Pattern settings ===
    this.patternScale = options.patternScale || { x: 1.0, y: 1.0, z: 1.0 };
    if (typeof this.patternScale === 'number') {
      this.patternScale = { x: this.patternScale, y: this.patternScale, z: this.patternScale };
    }
    this.patternScaleLinked = options.patternScaleLinked !== false;
    this.patternOffset = options.patternOffset || { x: 0, y: 0 };
    this.patternRotation = options.patternRotation || 0; // Rotation in degrees

    // === Color settings ===
    this.colorOpacity = options.colorOpacity || 1.0;

    // === Special effects settings ===
    this.effectScale = options.effectScale || { x: 1.0, y: 1.0, z: 1.0 };
    if (typeof this.effectScale === 'number') {
      this.effectScale = { x: this.effectScale, y: this.effectScale, z: this.effectScale };
    }
    this.effectScaleLinked = options.effectScaleLinked !== false;
    this.effectOffset = options.effectOffset || { x: 0, y: 0 };
    this.effectRotation = options.effectRotation || 0; // Rotation in degrees for rotated-metallic

    // === Text triplanar settings ===
    this.textScale = options.textScale || { x: 1.0, y: 1.0, z: 1.0 };
    if (typeof this.textScale === 'number') {
      this.textScale = { x: this.textScale, y: this.textScale, z: this.textScale };
    }
    this.textScaleLinked = options.textScaleLinked !== false;
    this.textOffset = options.textOffset || { x: 0, y: 0, z: 0 };
    this.textAxis = options.textAxis || 'xz'; // Project on: 'x', 'y', 'z', 'xy', 'xz', 'yz', or 'xyz'
  }

  /**
   * Get uniform scale value (average of x, y, z)
   * @param {string} type - Scale type: 'texture', 'video', 'gradient', 'pattern', 'effect'
   * @return {number} Average scale value
   */
  getUniformScale(type) {
    const scale = this[`${type}Scale`];
    if (typeof scale === 'number') return scale;
    return (scale.x + scale.y + scale.z) / 3;
  }

  /**
   * Set scale value, respecting linked state
   * @param {string} type - Scale type
   * @param {string} axis - Axis: 'x', 'y', or 'z'
   * @param {number} value - New scale value
   */
  setScale(type, axis, value) {
    const scaleProp = `${type}Scale`;
    const linkedProp = `${type}ScaleLinked`;

    if (this[linkedProp]) {
      // Linked: set all axes to same value
      this[scaleProp] = { x: value, y: value, z: value };
    } else {
      // Unlinked: set only specified axis
      this[scaleProp][axis] = value;
    }
  }

  /**
   * Serialize a face reference for Set storage
   * @param {string} cuboidKey - The cuboid key
   * @param {string} face - The face name
   * @return {string} Serialized face reference
   */
  static serializeFace(cuboidKey, face) {
    return `${cuboidKey}|${face}`;
  }

  /**
   * Deserialize a face reference
   * @param {string} serialized - Serialized face reference
   * @return {Object} { cuboidKey, face }
   */
  static deserializeFace(serialized) {
    const separatorIndex = serialized.lastIndexOf('|');
    const cuboidKey = serialized.substring(0, separatorIndex);
    const face = serialized.substring(separatorIndex + 1);
    return { cuboidKey, face };
  }

  /**
   * Add a face to this group
   * @param {string} cuboidKey - The cuboid key
   * @param {string} face - The face name
   */
  addFace(cuboidKey, face) {
    this.faces.add(MaterialGroup.serializeFace(cuboidKey, face));
  }

  /**
   * Remove a face from this group
   * @param {string} cuboidKey - The cuboid key
   * @param {string} face - The face name
   */
  removeFace(cuboidKey, face) {
    this.faces.delete(MaterialGroup.serializeFace(cuboidKey, face));
  }

  /**
   * Check if a face is in this group
   * @param {string} cuboidKey - The cuboid key
   * @param {string} face - The face name
   * @return {boolean} True if face is in group
   */
  hasFace(cuboidKey, face) {
    return this.faces.has(MaterialGroup.serializeFace(cuboidKey, face));
  }

  /**
   * Get all faces in this group
   * @return {Array} Array of { cuboidKey, face } objects
   */
  getFaces() {
    return Array.from(this.faces).map(s => MaterialGroup.deserializeFace(s));
  }

  /**
   * Get the number of faces in this group
   * @return {number} Face count
   */
  get size() {
    return this.faces.size;
  }

  /**
   * Clear all faces from this group
   */
  clear() {
    this.faces.clear();
  }
}
